import { Box, Button, CircularProgress, LinearProgress, Typography } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';

// Maps a stage key to its display label — the only place a future stage
// (e.g. 'xmp') needs to be registered for the list to render an extra column.
const STAGE_LABELS = { cover: 'Cover' };

// Compact status/action indicator only — no variable-length text here, so it
// never has to compete for width with the full-width lines below the row
// (dimension summary, error message). That competition is what caused the
// overlapping/garbled layout when a message got long.
function StageAction({ stage, onDownload, onRetry }) {
  switch (stage.status) {
    case 'queued':
      return <Typography variant="body2" color="text.secondary">Queued</Typography>;

    case 'uploading':
      return (
        <Box sx={{ minWidth: 140 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>{`Uploading… ${stage.progressPct}%`}</Typography>
          <LinearProgress variant="determinate" value={stage.progressPct} />
        </Box>
      );

    case 'processing':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={18} color="success" />
          <Typography variant="body2">Processing…</Typography>
        </Box>
      );

    case 'done':
      return (
        <Button
          variant="contained"
          color="success"
          size="small"
          startIcon={<CloudDownloadIcon />}
          onClick={onDownload}
        >
          Download
        </Button>
      );

    case 'error':
      return (
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
        >
          Retry
        </Button>
      );

    default:
      return null;
  }
}

// Shown whenever the backend measured both files, regardless of whether they
// matched — not just when there's a discrepancy to explain. Full row width,
// wraps freely, so a long value never has to fight another element for space.
function DimensionSummary({ dimensions }) {
  if (!dimensions) return null;
  const {
    contentWidthMm, contentHeightMm, coverWidthPx, coverHeightPx,
    contentAspectRatio, coverAspectRatio, diffPercent, matches,
  } = dimensions;
  return (
    <Typography variant="caption" color={matches ? 'text.secondary' : 'error'} sx={{ display: 'block', ml: 4.5 }}>
      {`PDF ${contentWidthMm.toFixed(1)}×${contentHeightMm.toFixed(1)}mm (${contentAspectRatio.toFixed(2)}) · `
        + `Cover ${coverWidthPx}×${coverHeightPx}px (${coverAspectRatio.toFixed(2)}) · Δ${diffPercent.toFixed(2)}%`}
    </Typography>
  );
}

function getRowBackgroundColor(entry) {
  const statuses = Object.values(entry.stages).map((s) => s.status);
  if (statuses.some((s) => s === 'error')) return 'rgba(244,67,54,0.05)';
  if (statuses.every((s) => s === 'done')) return 'rgba(76,175,80,0.05)';
  return 'white';
}

function CoverFileListRow({ entry, isLast, onDownload, onRetry }) {
  const coverStage = entry.stages.cover;

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: isLast ? 'none' : '1px solid #eee',
        backgroundColor: getRowBackgroundColor(entry),
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
          <PictureAsPdfIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
          <Typography noWrap title={entry.filename}>{entry.filename}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexShrink: 0 }}>
          {Object.entries(entry.stages).map(([stageName, stage]) => (
            <Box key={stageName} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {STAGE_LABELS[stageName] || stageName}
              </Typography>
              <StageAction
                stage={stage}
                onDownload={() => onDownload(entry, stageName)}
                onRetry={() => onRetry(entry, stageName)}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <DimensionSummary dimensions={coverStage?.dimensionInfo} />

      {coverStage?.status === 'error' && coverStage.error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4.5, mt: 0.5 }}>
          {coverStage.error}
        </Typography>
      )}
    </Box>
  );
}

function CoverFileList({ files, onDownload, onRetry }) {
  if (files.length === 0) return null;

  return (
    <Box sx={{ border: '1px solid #ddd', borderRadius: 1, mb: 3 }}>
      {files.map((entry, index) => (
        <CoverFileListRow
          key={entry.id}
          entry={entry}
          isLast={index === files.length - 1}
          onDownload={onDownload}
          onRetry={onRetry}
        />
      ))}
    </Box>
  );
}

export default CoverFileList;
